import React from 'react';
import Icon from '@mdi/react';
import { BookmarkBorderOutlined, ChevronRight, ExpandMore } from '@mui/icons-material';
import { TreeView, TreeItem } from '@mui/lab';
import { GlobalVariables } from '../../../../pdf/GlobalVariables';
import { Button, Stack } from '@mui/material';
import { mdiCollapseAll, mdiExpandAll } from '@mdi/js';
import styles from './Bookmarks.module.scss';
import i18next from 'i18next';
import type { Core } from '@pdftron/webviewer';
import { debuglog } from 'util';
import { ApryseSDK } from '../../../../pdf/ApryseSDK';
import { devLog } from '../../../../utils';

export default () => {
    const [rootBookmarks, setRootBookmarks] = React.useState<Core.Bookmark[]>([]);
    const [refreshState, toggleRefreshState] = React.useReducer(() => Math.random(), 0);
    const forceRefresh = React.useCallback(() => toggleRefreshState(), []);
    const [expanded, setExpanded] = React.useState<string[]>([]);

    const onNodeToggle = (_: React.ChangeEvent<{}>, nodeIds: string[]) => {
        setExpanded(nodeIds);
    };

    function onBookmarkSelect(event: React.ChangeEvent<{}>, nodeId: string) {
        if ((event.target as HTMLElement).localName === 'svg') {
            devLog('svg');
            return;
        }

        const bookmark = GlobalVariables.bookmarks.filter(
            bookmark => bookmark.getObjNum().toString() === nodeId,
        )[0];

        if (!bookmark) {
            debuglog(`Did not find bookmark with id ${nodeId}`);
            return;
        }

        const currentScale = ApryseSDK.documentViewer.getZoomLevel();
        const offset = Array.from({ length: bookmark.getPageNumber() - 1 }, (_, i) => i + 1)
            .map(i => (ApryseSDK.documentViewer.getPageHeight(i) + 8) * currentScale)
            .reduce((a, b) => a + b, 0);
        ApryseSDK.documentViewer.zoomTo(
            currentScale,
            bookmark.getHorizontalPosition(),
            offset + bookmark.getVerticalPosition(),
        );
    }

    function expandAll() {
        setExpanded(GlobalVariables.bookmarks.map(bookmark => bookmark.getObjNum().toString()));
    }

    function collapseAll() {
        setExpanded([]);
    }

    React.useMemo(() => {
        GlobalVariables.bookmarks = [];
        ApryseSDK.documentViewer
            .getDocument()
            .getBookmarks()
            .then(bookmarks => {
                GlobalVariables.bookmarks = bookmarks;
                setRootBookmarks([...bookmarks]);
            });
    }, [refreshState]);

    const renderBookmark = (bookmark: Core.Bookmark, level: number) => {
        if (
            !GlobalVariables.bookmarks.some(
                existing => existing.getObjNum() === bookmark.getObjNum(),
            )
        ) {
            GlobalVariables.bookmarks.push(bookmark);
        }

        if (!bookmark.getChildren()) {
            return (
                <TreeItem
                    nodeId={bookmark.getObjNum().toString()}
                    label={bookmark.getName()}
                    className={styles.resultItem}
                />
            );
        }

        return (
            <TreeItem
                nodeId={bookmark.getObjNum().toString()}
                label={bookmark.getName()}
                className={styles.resultItem}
            >
                {bookmark.getChildren().map(child => renderBookmark(child, level + 1))}
            </TreeItem>
        );
    };

    const renderBookmarks = React.useMemo(() => {
        return rootBookmarks.map(bookmark => renderBookmark(bookmark, 0));
    }, [rootBookmarks]);

    const renderMenu = React.useMemo(() => {
        return (
            <Stack className={styles.sidebarMenuArea} direction="row" spacing={1} pl={2} pr={2}>
                <Button
                    id={GlobalVariables.isForTestRun ? 'bookmarksSidebarMenuBtnExpand' : undefined}
                    className={styles.iconButton}
                    variant="outlined"
                    key="expand"
                    title={i18next.t('sidebar.popups.layers.expand')}
                    onClick={() => expandAll()}
                >
                    <Icon path={mdiExpandAll} />
                </Button>
                <Button
                    id={
                        GlobalVariables.isForTestRun ? 'bookmarksSidebarMenuBtnCollapse' : undefined
                    }
                    className={styles.iconButton}
                    variant="outlined"
                    key="collapse"
                    title={i18next.t('sidebar.popups.layers.collapse')}
                    onClick={() => collapseAll()}
                >
                    <Icon path={mdiCollapseAll} />
                </Button>
            </Stack>
        );
    }, [refreshState]);

    React.useEffect(() => {
        // ToDO:
        // const events = GlobalVariables.docRender.pdfViewer.getEventEmitter();

        // events
        //     .on(PDFViewCtrl.PDF.constant.DataEvents.bookmarkAdded, forceRefresh)
        //     .on(PDFViewCtrl.PDF.constant.DataEvents.bookmarkRemoved, forceRefresh)
        //     .on(PDFViewCtrl.PDF.constant.DataEvents.bookmarkUpdated, forceRefresh);

        // return () => {
        //     events
        //         .off(PDFViewCtrl.PDF.constant.DataEvents.bookmarkAdded, forceRefresh)
        //         .off(PDFViewCtrl.PDF.constant.DataEvents.bookmarkRemoved, forceRefresh)
        //         .off(PDFViewCtrl.PDF.constant.DataEvents.bookmarkUpdated, forceRefresh);
        // };
    }, []);

    return (
        <div className={styles.resultList}>
            {renderMenu}
            <TreeView
                className={styles.list}
                expanded={expanded}
                defaultEndIcon={<BookmarkBorderOutlined />}
                defaultCollapseIcon={<ExpandMore />}
                defaultExpandIcon={<ChevronRight />}
                onNodeToggle={onNodeToggle}
                onNodeSelect={onBookmarkSelect}
                multiSelect={false}
            >
                {renderBookmarks}
            </TreeView>
        </div>
    );
};
